import { initialRequests, alarmMapping } from '../Constants/constants';
import {
  AlarmInList,
  AlarmGetZoneIdAndZonePart,
  AddAlarmLogEntry,
  AddStructuredAlarmLogEntries,
  UpdateAlarmLog,
  MakeAlarmID,
  alarmDisplayName,
  alarmValueFormat,
  formatAlarmValue
} from '../Utils/alarm_utils.js';

import controllerOnMessage from './controllerOnMessageHook.js';
import { applyControllerApiVersion } from '../Utils/controller_metadata.js';
import { getControllerConnectionStatus } from '../Utils/controller_connection_utils.js';
import { websocketDebug } from '../Utils/websocket_logging.js';
import i18next from 'i18next';

function updateStructuredActiveAlarms(store, controller, instances, triggered) {
  for (const instance of instances ?? []) {
    const matches = (alarm) =>
      alarm.controller?.id === controller.id &&
      Number(alarm.identity?.zone_id) === Number(instance.zone_id) &&
      Number(alarm.identity?.alarm_type_id) === Number(instance.alarm_type_id) &&
      Number(alarm.identity?.sub_zone_or_id) === Number(instance.sub_zone_or_id);
    const previous = store.alarms.activeAlarms.find(matches);
    if (!triggered && !previous) continue;
    if (triggered && previous?.value === instance.value) continue;
    const catalog = store.alarm_catalog_state?.entries;
    const alarm = {
      controller,
      identity: {
        zone_id: instance.zone_id,
        alarm_type_id: instance.alarm_type_id,
        sub_zone_or_id: instance.sub_zone_or_id
      },
      name: catalog?.[instance.alarm_type_id]?.key,
      zone: instance.zone_id,
      value: instance.value
    };
    if (triggered) {
      if (previous) previous.value = instance.value;
      else store.alarms.activeAlarms.push(alarm);
    } else {
      store.alarms.activeAlarms = store.alarms.activeAlarms.filter((entry) => !matches(entry));
    }
    const name = alarmDisplayName(alarm, catalog, i18next.language, store.hooks.t);
    const [, zonePart] = AlarmGetZoneIdAndZonePart(controller, { zone: String(instance.zone_id) });
    const status = triggered ? (previous ? 'value_updated' : 'triggered') : 'resolved';
    store.hooks.enqueueSnackbar(
      `${controller.name}${zonePart} - ${name}: ${store.hooks.t(status)}`,
      { variant: triggered ? 'error' : 'success' }
    );
  }
}

export const INFO_GRAPH_RETRY_INTERVAL_MS = 15000;
export const INFO_GRAPH_REFRESH_INTERVAL_MS = 10 * 60 * 1000;
export const INFO_GRAPH_REFRESH_JITTER_MAX_MS = 30 * 1000;

function getInfoGraphRefreshJitter(connection) {
  if (!Number.isFinite(connection.info_graphs_refresh_jitter_ms)) {
    connection.info_graphs_refresh_jitter_ms = Math.floor(
      Math.random() * (INFO_GRAPH_REFRESH_JITTER_MAX_MS + 1)
    );
  }
  return connection.info_graphs_refresh_jitter_ms;
}

export function shouldRequestInfoGraphs(controller, now = Date.now()) {
  const connection = controller.connection;
  const requestedAt = Number(connection.info_graphs_requested_at) || 0;
  const receivedAt = Number(connection.info_graphs_received_at) || 0;
  const waitingForResponse =
    requestedAt > receivedAt && now - requestedAt < INFO_GRAPH_RETRY_INTERVAL_MS;
  if (waitingForResponse) return false;

  if (!connection.info_graphs_received || receivedAt === 0) return true;

  return now - receivedAt >= INFO_GRAPH_REFRESH_INTERVAL_MS + getInfoGraphRefreshJitter(connection);
}

export function requestInfoGraphs(wsManager, controller) {
  getInfoGraphRefreshJitter(controller.connection);
  const sent = wsManager.sendWSMsg(controller, 'get_info_graphs');
  console.debug('[info-graphs][request]', {
    controllerId: controller.id,
    sent,
    apiVersion: controller.settings?.system?.api_version ?? controller.api_version
  });
  if (sent) controller.connection.info_graphs_requested_at = Date.now();
  return sent;
}

export function sendInitialControllerRequests(wsManager, controller) {
  let sentAll = true;
  initialRequests(controller?.api_version).forEach((request) => {
    const sent =
      request === 'get_info_graphs'
        ? requestInfoGraphs(wsManager, controller)
        : wsManager.sendWSMsg(controller, request);
    sentAll = sentAll && sent;
  });
  controller.connection.requested_all = sentAll;
  return sentAll;
}

export default function globalOnMessage(wsManager, resp) {
  if (resp != null) {
    let command = '';
    try {
      command = resp['command'];
      websocketDebug('[global received]', {
        command,
        controllerId: resp.internal_id
      });
    } catch (err) {
      alert('Wrong Json format! Please contact admins!');
      return;
    }

    if (command === 'reset_user') {
      wsManager.store.hooks.setNoConnection(false);
      wsManager.suspend('authentication-failed');
      wsManager.store.hooks.onAuthenticationFailed?.();
      return;
    }

    const controller_id = resp.internal_id;
    const cur_controller = wsManager.store.getControllerById(controller_id);
    const controller_name = cur_controller !== undefined ? cur_controller.name : controller_id;

    if (command === 'update_display_name') {
      if (cur_controller) cur_controller.name = resp.data.display_name;
      wsManager.store.refreshControllerMetadata?.({ invalidate: true });
      return;
    }

    // Version notifications may arrive before the initial HTTP controller list.
    if (command === 'controller_api_version') {
      const version = resp.data?.id;
      if (String(version ?? '').trim() !== '') {
        wsManager.store.recordControllerApiVersion?.(controller_id, version);
        applyControllerApiVersion(cur_controller, version);
        if (cur_controller) cur_controller.connection.request_values_full = false;
        wsManager.store.refreshControllerMetadata?.({ invalidate: true });
      }
      return;
    }

    const controllerScoped =
      'internal_id' in resp &&
      command !== 'resp_controllers_active' &&
      command !== 'resp_initialize';
    if (controllerScoped && cur_controller === undefined) {
      websocketDebug('[global received] ignored before controller registration', {
        command,
        controllerId: controller_id
      });
      return;
    }

    const alarms = wsManager.store.alarms;
    const curActiveAlarms = alarms.activeAlarms;
    const timestamp = resp['data']?.['timestamp'];

    // cmd: resp_controllers_active:
    if (command === 'resp_controllers_active') {
      if ('data' in resp && resp['data'] != null && 'active_controllers' in resp['data']) {
        const active_controllers = resp['data']['active_controllers'];
        websocketDebug('[global received] controllers_active', {
          count: Object.keys(active_controllers).length
        });

        for (let key of Object.keys(active_controllers)) {
          const controller = wsManager.store.getControllerById(key);

          if (controller) {
            const previousStatus = controller?.connection?.status ?? 'bad';
            const new_status = getControllerConnectionStatus(
              active_controllers[key] === true,
              controller.connection.last_received
            );
            const controllerReconnected = previousStatus === 'bad' && new_status !== 'bad';

            // Only update if status actually changed.
            if (previousStatus !== new_status) {
              controller.connection.status = new_status;
            }

            if (controllerReconnected) {
              // The ESP may now run a different firmware. Request authoritative metadata from
              // the server and force a complete settings response from the controller.
              controller.connection.request_values_full = false;
              wsManager.store.refreshControllerMetadata?.({ invalidate: true });
            }

            if (!controller.connection.requested_all) {
              console.log('[wsManager]');
              sendInitialControllerRequests(wsManager, controller);
            } else if (shouldRequestInfoGraphs(controller)) {
              requestInfoGraphs(wsManager, controller);
            }
          }
        }
      }
    }

    // cmd: alarms_triggered:
    else if (command === 'alarms_triggered') {
      updateStructuredActiveAlarms(
        wsManager.store,
        cur_controller,
        resp.data.alarm_instances,
        true
      );
      AddStructuredAlarmLogEntries(
        alarms.alarmsLog,
        resp.data.alarm_instances,
        cur_controller,
        timestamp,
        true
      );
      // unpack received data.
      const new_alarms = resp.data.alarms ?? {};
      const [zone_id, zone_part] = AlarmGetZoneIdAndZonePart(cur_controller, resp['data']);

      // Iterate over all received alarms and add alarm to active alarms.
      for (const [alarm_id, value] of Object.entries(new_alarms)) {
        const displayValue =
          alarmMapping[alarm_id]?.unit === 'hPa'
            ? formatAlarmValue(
                value,
                alarmValueFormat(alarm_id, { scale: -1, unit: 'hPa' }, cur_controller)
              )
            : value;
        const alarm_joined_id = MakeAlarmID(cur_controller.id, alarm_id, zone_id);
        const cur_alarm = curActiveAlarms.find((x) => x.id === alarm_joined_id);

        if (cur_alarm === undefined || cur_alarm === null) {
          wsManager.store.alarms.activeAlarms.push({
            controller: cur_controller,
            id: alarm_joined_id,
            name: alarm_id,
            value: value,
            zone: zone_id
          });
          wsManager.store.hooks.enqueueSnackbar(
            controller_name +
              zone_part +
              ' - Alarm "' +
              alarmDisplayName(
                { name: alarm_id },
                wsManager.store.alarm_catalog_state?.entries,
                i18next.language,
                wsManager.store.hooks.t
              ) +
              '" ' +
              wsManager.store.hooks.t('triggered') +
              ' (' +
              displayValue +
              ')!',
            { variant: 'error' }
          );
        } else {
          wsManager.store.hooks.enqueueSnackbar(
            controller_name +
              zone_part +
              ' - Alarm "' +
              alarmDisplayName(
                { name: alarm_id },
                wsManager.store.alarm_catalog_state?.entries,
                i18next.language,
                wsManager.store.hooks.t
              ) +
              '" ' +
              wsManager.store.hooks.t('value_updated') +
              ': ' +
              displayValue +
              '!',
            { variant: 'error' }
          );
        }
        // Add to alarm-log.
        AddAlarmLogEntry(alarms.alarmsLog, {
          triggered: true,
          controller: cur_controller,
          id: alarm_id,
          ts: timestamp,
          value: value,
          zone: zone_id
        });
      }

      alarms.unreadAlarms = true;
    }

    // cmd: alarms_resolved:
    else if (command === 'alarms_resolved') {
      updateStructuredActiveAlarms(
        wsManager.store,
        cur_controller,
        resp.data.alarm_instances,
        false
      );
      AddStructuredAlarmLogEntries(
        alarms.alarmsLog,
        resp.data.alarm_instances,
        cur_controller,
        timestamp,
        false
      );
      // unpack received data.
      const resolvedAlarms = resp.data.alarm_keys ?? [];
      const [zone_id, zone_part] = AlarmGetZoneIdAndZonePart(cur_controller, resp['data']);

      // Iterate over all current alarms and add alarm to active alarms.
      alarms.activeAlarms = alarms.activeAlarms.filter((alarm) => {
        // Return whether alarm shall be removed.
        const alarm_in_list =
          AlarmInList(alarm.id, resolvedAlarms, controller_id, zone_id) === false;
        if (!alarm_in_list)
          wsManager.store.hooks.enqueueSnackbar(
            controller_name +
              zone_part +
              ' - Alarm "' +
              alarmDisplayName(
                alarm,
                wsManager.store.alarm_catalog_state?.entries,
                i18next.language,
                wsManager.store.hooks.t
              ) +
              '" ' +
              wsManager.store.hooks.t('resolved') +
              '!',
            { variant: 'success' }
          );
        return alarm_in_list;
      });

      // Add to alarm-log.
      for (let i = 0; i < resolvedAlarms.length; i++) {
        AddAlarmLogEntry(alarms.alarmsLog, {
          triggered: false,
          controller: cur_controller,
          id: resolvedAlarms[i],
          ts: timestamp,
          zone: zone_id
        });
      }

      alarms.unreadAlarms = alarms.activeAlarms.length > 0;
    }

    // cmd: send_full_alarm_log:
    else if (command === 'send_full_alarm_log') {
      const data = resp.data ?? {};
      const log =
        data.alarm_log ??
        (data.zones
          ? Object.entries(data.zones)
              .flatMap(([zone, history]) =>
                (history.alarm_log ?? []).map((entry) => ({
                  ...entry,
                  zone_id: entry.zone_id ?? Number(zone)
                }))
              )
              .sort((a, b) => a.timestamp - b.timestamp)
          : undefined);
      alarms.alarmsLog = UpdateAlarmLog(alarms.alarmsLog, log, cur_controller);
    }

    // cmd: resp_initialize:
    else if (command === 'resp_initialize') {
      console.log('[global-ws, init] global initialization succeeded.');
      wsManager.store.hooks.setNoConnection(false);
      wsManager.markConnected();
      wsManager.start();
      wsManager.store.refreshControllerMetadata?.({ invalidate: true });
    }

    // cmd: unkown command.
    else if ('internal_id' in resp) {
      controllerOnMessage(wsManager, resp);
    }
  }
}
